(() => {
  if (customElements.get('kt-display-currency')) return;

  class KtDisplayCurrency extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      const settings = this.querySelector('script[type="application/json"]');
      if (!settings) return;
      try {
        this.config = JSON.parse(settings.textContent);
      } catch {
        return;
      }
      this.config.native = String(this.config.native || '').trim().toUpperCase();
      this.config.display = String(this.config.display || this.config.native).trim().toUpperCase();
      if (!/^[A-Z]{3}$/.test(this.config.native) || !/^[A-Z]{3}$/.test(this.config.display)) return;
      this.started = true;
      this.originals = new WeakMap();
      this.formatters = new Map();
      this.state = 'native';
      this.cacheKey = 'kt-display-rates-v1-USD';
      this.retryKey = 'kt-display-rates-retry-v1';
      this.observer = new MutationObserver(() => this.schedule());
      this.observe();
      this.onVisible = () => {
        if (document.visibilityState === 'visible') this.refresh();
      };
      document.addEventListener('visibilitychange', this.onVisible);
      this.refresh();
    }

    disconnectedCallback() {
      this.started = false;
      this.observer?.disconnect();
      this.request?.abort();
      document.removeEventListener('visibilitychange', this.onVisible);
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    observe() {
      if (!this.started) return;
      this.observer.observe(document.body, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['data-kt-money', 'data-kt-currency', 'data-kt-prefix', 'data-kt-suffix', 'data-geolizr-price', 'doubly-currency']
      });
    }

    rate(code, data = this.rates) {
      const rates = data?.rates;
      if (!rates) return NaN;
      if (code === 'ANG') return rates.ANG || rates.XCG;
      if (code === 'SLL') return rates.SLL || rates.SLE * 1000;
      if (code === 'STD') return rates.STD || rates.STN * 1000;
      return rates[code];
    }

    valid(data) {
      const now = Date.now() / 1000;
      return data?.result === 'success' && data.base_code === 'USD' &&
        Number.isFinite(data.time_last_update_unix) && data.time_last_update_unix <= now + 300 &&
        now - data.time_last_update_unix < 172800 &&
        (!data.time_eol_unix || data.time_eol_unix > now) &&
        [this.config.native, this.config.display].every(code => Number.isFinite(this.rate(code, data)) && this.rate(code, data) > 0);
    }

    readStorage(key) {
      try { return JSON.parse(localStorage.getItem(key)); } catch { return null; }
    }

    writeStorage(key, value) {
      try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* Browsing still works without storage. */ }
    }

    async refresh() {
      if (this.loading || !this.started) return;
      const page = String(this.config.page || '');
      if (this.config.native === this.config.display || page.startsWith('customers/') || page === 'gift_card') {
        this.state = 'native';
        this.render();
        return;
      }

      const cached = this.readStorage(this.cacheKey);
      if (this.valid(cached)) {
        this.rates = cached;
        this.state = 'converted';
        this.render();
        const refreshAt = Number.isFinite(cached.time_next_update_unix)
          ? Math.min(cached.time_next_update_unix, cached.time_last_update_unix + 86400)
          : cached.time_last_update_unix + 86400;
        if (Date.now() / 1000 < refreshAt) {
          this.scheduleRefresh(refreshAt);
          return;
        }
      }
      if (Number(this.readStorage(this.retryKey)) > Date.now()) {
        this.state = this.valid(this.rates) ? 'converted' : 'unavailable';
        this.render();
        this.scheduleRefresh(Date.now() / 1000 + 3600);
        return;
      }

      this.loading = true;
      this.request = new AbortController();
      const timeout = setTimeout(() => this.request?.abort(), 5000);
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD', {
          signal: this.request.signal,
          credentials: 'omit',
          referrerPolicy: 'no-referrer'
        });
        if (!response.ok) throw new Error('Currency rates unavailable');
        const rates = await response.json();
        if (!this.valid(rates)) throw new Error('Currency rates invalid');
        if (!this.started) return;
        this.rates = rates;
        this.writeStorage(this.cacheKey, rates);
        this.writeStorage(this.retryKey, 0);
        this.state = 'converted';
        this.scheduleRefresh(rates.time_next_update_unix);
      } catch {
        if (!this.started) return;
        this.writeStorage(this.retryKey, Date.now() + 3600000);
        this.state = this.valid(this.rates) ? 'converted' : 'unavailable';
        this.scheduleRefresh(Date.now() / 1000 + 3600);
      } finally {
        clearTimeout(timeout);
        this.loading = false;
        this.request = null;
        if (this.started) this.render();
      }
    }

    scheduleRefresh(next) {
      clearTimeout(this.timer);
      const delay = Number.isFinite(next)
        ? Math.max(3600000, Math.min(86400000, next * 1000 - Date.now()))
        : 3600000;
      this.timer = setTimeout(() => this.refresh(), delay);
    }

    schedule() {
      if (this.frame || !this.started) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        if (this.started) this.render();
      });
    }

    format(amount, currency) {
      if (!this.formatters.has(currency)) {
        let formatter;
        try {
          formatter = new Intl.NumberFormat(this.config.locale, { style: 'currency', currency, currencyDisplay: 'code' });
        } catch {
          formatter = new Intl.NumberFormat('en', { style: 'currency', currency, currencyDisplay: 'code' });
        }
        this.formatters.set(currency, formatter);
      }
      return this.formatters.get(currency).format(amount);
    }

    setText(node, text) {
      if (node.textContent !== text) node.textContent = text;
    }

    translate(text) {
      return String(text || '').replaceAll('__NATIVE__', this.config.native).replaceAll('__DISPLAY__', this.config.display);
    }

    render() {
      const active = this.state === 'converted' && this.valid(this.rates);
      const currency = active ? this.config.display : this.config.native;
      const unavailable = this.state === 'unavailable' || (this.state === 'converted' && !active);
      this.dataset.state = active ? 'converted' : unavailable ? 'unavailable' : 'native';
      this.dataset.currency = currency;

      // Our own text changes must not schedule another render. External AJAX changes remain observed.
      this.observer.disconnect();
      try {
        // Dr Stacked Discounts replaces the native cart summary with its own money nodes.
        // Its source cents and source currency remain authoritative after display conversion.
        document.querySelectorAll('.docapp-widget .money[data-geolizr-price]').forEach(node => {
          const amount = (node.getAttribute('data-geolizr-price') || '').trim();
          const source = (node.getAttribute('doubly-currency') || '').trim().toUpperCase() || this.config.native;
          if (amount === '' || !Number.isFinite(Number(amount)) || !/^[A-Z]{3}$/.test(source)) {
            delete node.dataset.ktMoney;
            delete node.dataset.ktCurrency;
            return;
          }
          node.dataset.ktMoney = amount;
          node.dataset.ktCurrency = source;
        });
        document.querySelectorAll('[data-kt-money]').forEach(node => {
          const rawAmount = node.dataset.ktMoney;
          const amount = Number(rawAmount);
          const source = (node.dataset.ktCurrency || this.config.native).trim().toUpperCase();
          if (rawAmount === '' || !Number.isFinite(amount) || !/^[A-Z]{3}$/.test(source)) return;
          const prefix = node.dataset.ktPrefix || '';
          const suffix = node.dataset.ktSuffix || '';
          if (!this.originals.has(node)) this.originals.set(node, { html: node.innerHTML, amount, source, prefix, suffix });
          const original = this.originals.get(node);
          const sourceRate = this.rate(source);
          if (active && Number.isFinite(sourceRate) && sourceRate > 0) {
            this.setText(node, prefix + this.format(amount / 100 * this.rate(currency) / sourceRate, currency) + suffix);
          } else if (original.amount === amount && original.source === source && original.prefix === prefix && original.suffix === suffix) {
            if (node.innerHTML !== original.html) node.innerHTML = original.html;
          } else {
            this.setText(node, prefix + this.format(amount / 100, source) + suffix);
          }
        });

        document.querySelectorAll('.kt-currency-trigger__label').forEach(node => {
          const country = node.dataset.ktCountryName;
          this.setText(node, country ? `${country} (${currency})` : currency);
        });
        document.querySelectorAll('[data-kt-currency-note]').forEach(node => {
          node.hidden = !unavailable;
          this.setText(node, this.translate(this.config.unavailable));
        });
        document.querySelectorAll('[data-kt-checkout-note]').forEach(node => {
          node.hidden = !active;
          this.setText(node, this.translate(this.config.checkoutNote));
        });
        document.querySelectorAll('[data-kt-checkout-total]').forEach(node => { node.hidden = !active; });
        document.querySelectorAll('[data-kt-filter-note]').forEach(node => {
          node.hidden = !active;
          this.setText(node, this.translate(this.config.filterNote));
        });
        document.querySelectorAll('[data-kt-currency-attribution]').forEach(node => { node.hidden = !active; });
      } finally {
        this.observe();
      }
    }
  }

  customElements.define('kt-display-currency', KtDisplayCurrency);
})();
